import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AccordionGroupMeta } from '@cudirect/shared-ui-library';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { Disclosure, DisclosureApplicant } from '@workflow/ecp-workflow/dynamic-app/types/disclosure.type';
export interface DisclosuresGroup {
  eaAccordions?: { open: () => void; close: () => void }[];
}
/** component to show available accounts and services */
@BuildingBlock('smart-sign')
@Component({
  selector: 'ecp-smartsign',
  styleUrls: ['./smart-sign.component.scss'],
  templateUrl: './smart-sign.component.html',
  standalone: false
})
export class SmartSignComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  /**
   * Accordion group meta data
   */
  public accordionGroupMeta = new AccordionGroupMeta();

  /**
   * Accordion disclosures
   */
  public content = '';
  public disclosures: Disclosure[] = [];
  public disclosure: Disclosure = {} as Disclosure;
  private subs: Subscription[] = [];
  public filename: string;
  public docTitle: string;
  public isOpen = false;

  private isCallInitiated = false;
  public isDataloaded = false;
  public pdfURL = '';
  public isOlaFlow = false;
  public isOAOFlow = false;
  public isPageSubmitted = false;
  public chbx_id_prefix = 'smart_sign_disclosure_acc';
  public disclosure_panel_id = 'open-smart-sign-disclosure-panel';
  public hasPDF = false;
  public showValidationMessage = false;
  private previousCheckboxValues: Map<string, boolean> = new Map();
  private checkboxClickedValues: Map<string, boolean> = new Map();
  public applicantTypes = [
    { type: 'primaryApplicant', name: 'Primary Applicant' },
    { type: 'coApplicant', name: 'Co-Applicant' },
    { type: 'additionalCoApplicant', name: 'Co-Applicant' },
    { type: 'borrower4', name: 'Co-Applicant' }
  ];
  public isYouthMembership = false;
  public showVerificationHelpText = false;
  public get isAllApplicantsVerified(): boolean {
    // ignore youth member
    if (this.isYouthMembership)
      return this.disclosure.applicants.filter((a, i) => i !== 0).every((a) => a.applicant && a.isVerified && a.accepted);
    else return this.disclosure.applicants.every((a) => a.applicant && a.isVerified && a.accepted);
  }

  @ViewChild('disclosuresGroup') public disclosuresGroup: DisclosuresGroup;

  /** On Init */
  public ngOnInit(): void {
    this.isYouthMembership = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').value as boolean;
    this.isOlaFlow = this.workflowService?.currentFlow?.id === 'ola' || this.workflowService?.currentFlow?.id.indexOf('ola') !== -1;
    this.isOAOFlow = this.workflowService?.currentFlow?.id === 'oao' || this.workflowService?.currentFlow?.id.indexOf('oao') !== -1;
    this.onLoadAsync(() => {
      this.disclosures =
        this.field && this.field.config && this.field.config.content ? JSON.parse(this.field.config.content.toString()) : [];
      let nxtIndex = -1;
      this.disclosure = this.disclosures[0];
      this.disclosures.forEach((element, index) => {
        if (this.userService.isAuthenticated) element.applicants[0].isVerified = true;
        const anyUnCheckedApplicant = element.applicants.filter((f) => !f.accepted).length > 0;
        if (nxtIndex === -1 && anyUnCheckedApplicant) {
          nxtIndex = index;
          element.isOpen = true;
        } else {
          element.isOpen = false;
        }
        if (typeof element.allowToAccept == 'string' && element.allowToAccept.includes('api:')) {
          element.allowToAccept = this.statefulService.wrap(element.allowToAccept, undefined, index);
        }
        this.showVerificationHelpText = this.showVerificationHelpText || element?.applicants?.some((a) => !a.isVerified);
      });
    });
    this.updateValidity();
    this.pageSubmitted$.subscribe((value) => {
      this.isPageSubmitted = value;
      if (this.isPageSubmitted) {
        this.setDisclosure();
      }
    });
  }

  /**
   * Focus 1st Invalid disclosure checkbox
   */
  public setDisclosure(): void {
    let firstInvalidId: string;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.disclosures.forEach((disclosure, index) => {
      disclosure.applicants.forEach((applicant, applicantIndex) => {
        if (!applicant.accepted) {
          if (applicantIndex === 0) {
            // Get only the 1st invalid control id, skip other invalid id
            if (!firstInvalidId) {
              firstInvalidId = `${this.chbx_id_prefix}_${applicantIndex}`;
              this.hasPDF = disclosure.allowToAccept ? false : Boolean(disclosure.isPDFFile);
            }
            //  element.open();
          }
        }
      });

      if (firstInvalidId) {
        setTimeout(() => {
          if (this.hasPDF) firstInvalidId = this.disclosure_panel_id;
          this.focusService.setFocus(firstInvalidId);
        }, 0);
      }
    });
  }

  public getApplicantLabel(label: string): string {
    const parseToken = '#{';
    const parsedText = this.stringVariableService.parse(label, this.field.iterationId, this.field.pageIterationId, '#{')?.toString();
    if (parsedText.includes(parseToken)) {
      const left = label.indexOf(parseToken);
      const right = label.lastIndexOf('}');
      const searchStr = label.substring(left, right + 1);
      return label.replace(searchStr, 'Applicant');
    }
    return parsedText;
  }

  public isApplicantExists(applicant): boolean {
    return applicant ? true : false;
  }

  // Load disclosure asyn, bcze in some cases fetch disclosures from api call
  protected readonly onLoadAsync = (cb: () => void): void => {
    if (this.field?.config?.onLoad) {
      const config = this.logicService.evaluate<Promise<boolean>>(
        this.field.config.onLoad,
        {
          field: this.field,
          page: this.workflowService.currentPage
        },
        'single'
      );
      if (config && config[0]) {
        config[0].then(cb);
      } else {
        cb();
      }
    }
  };

  /**
   * Determines the CSS class to display based on the disclosure selection status.
   *
   * @remarks
   * - If required validation is active (`showRequiredValidationMessage` returns `true`)
   *   and the page has been submitted (`isPageSubmitted` is `true`), the method checks
   *   whether all disclosures are accepted.
   * - If there are unaccepted disclosures, it returns a warning CSS class.
   * - Otherwise, it returns an empty string.
   *
   * @returns The CSS class for the disclosure status:
   * - `'cu-warning-solid bl-font-color--error'` if there are unaccepted disclosures and the page has been submitted.
   * - An empty string if all disclosures are accepted or validation conditions are not met.
   */
  public getDisclosureSelectedStatus(): string {
    const disclosureAccepted = this.disclosures.filter((t) => !t.accepted && !this.allApplicantsAccepted(t.applicants)).length === 0;
    if (!disclosureAccepted && this.isPageSubmitted) {
      return 'cu-warning-solid bl-font-color--error';
    }
    return '';
  }

  /**
   * Checks if all disclosures and their associated applicants are accepted.
   *
   * @remarks
   * - A disclosure is considered accepted if its `accepted` property is `true`
   *   and the method `allApplicantsAccepted` returns `true` for its applicants.
   * - If any disclosure fails these conditions, the method returns `false`.
   *
   * @returns `true` if all disclosures and their associated applicants are accepted; otherwise, `false`.
   */
  public allDisclosuresAccepted(): boolean {
    return this.disclosures.filter((t) => !t.accepted && !this.allApplicantsAccepted(t.applicants)).length === 0;
  }

  public requiredValidation(): boolean {
    return this.showRequiredValidationMessage() && this.isPageSubmitted;
  }

  public updateValidity(): void {
    if (this.disclosures && this.disclosures.length) {
      const disclosureAccepted = this.disclosures.filter((t) => !t.accepted && !this.allApplicantsAccepted(t.applicants)).length === 0;
      this.onChange(disclosureAccepted);
    }
  }

  public allApplicantsAccepted(applicants): boolean {
    // ignore youth member
    if (this.isYouthMembership) return applicants && applicants.filter((t, i) => i !== 0 && !t.accepted).length === 0;
    else return applicants && applicants.filter((t) => !t.accepted).length === 0;
  }

  public showRequiredValidationMessage(): boolean {
    this.showValidationMessage = this.disclosure?.isReadToAccept && !this.isAllDocsViewed();
    return this.showValidationMessage;
  }

  public disableApplicantCheckbox(applicant: DisclosureApplicant): boolean {
    return (this.disclosure?.isReadToAccept && !this.isAllDocsViewed()) || !applicant.isVerified;
  }

  public disableVerifyIdentityButton(): boolean {
    return this.disclosures.find((d) => d.isReadToAccept) && !this.isAllDocsViewed() ? true : false;
  }

  /**
   * Check allow to invoke click event.?
   * If View Required Message is displaying then disable click event
   */
  public get enableClickEvent(): boolean {
    return this.showRequiredValidationMessage();
  }

  /**
   * Updates the acceptance status of an applicant based on the checkbox interaction.
   *
   * @param applicants - An array of applicants whose acceptance status is being managed.
   * @param itemIndex - A unique identifier for the checkbox associated with the applicant.
   * @param index - The index of the applicant in the `applicants` array.
   * @param event - The event triggered by the checkbox interaction.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAcceptance(applicants: any[], itemIndex: string, index: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const applicant = applicants[index];
    const originalValue = this.previousCheckboxValues.get(itemIndex);
    if (this.isAllDocsViewed() || this.disclosure.isReadToAccept === false) {
      inputElement.checked = applicant.accepted;
    } else {
      applicant.accepted = false;
      inputElement.checked = false;
    }

    this.applicantAcceptance(index, applicant.accepted);
    this.updateValidity();
    if (originalValue !== true) {
      this.checkboxClickedValues.set(itemIndex, true);
    }
  }

  /**
   * Stores the original value of a checkbox when it gains focus.
   *
   * @param indexItem - A unique identifier for the checkbox.
   * @param event - The focus event triggered when the checkbox is focused.
   */
  storeOriginalCheckboxValue(indexItem: string, event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.previousCheckboxValues.set(indexItem, inputElement.checked);
  }

  private applicantAcceptance(index: number, accepted: boolean) {
    this.disclosures.forEach((t) => {
      t.applicants[index].accepted = accepted;
      if (t.applicants[index].storageId) {
        this.patchValue(t.applicants[index].storageId, t.applicants[index].accepted, index);
      }

      if (t.applicants[index].agreedDateStorageId && t.applicants[index].accepted) {
        this.patchValue(t.applicants[index].agreedDateStorageId, new Date().toISOString(), index);
      }
    });
  }

  public keepCheckboxFocus(index): void {
    // Keep selected checkbox checked/focused
    // ADA: Focus was getting lost when user toggled checkbox w/ keyboard
    const id = `${this.chbx_id_prefix}_${index}`;
    this.focusService.setFocus(id);
  }

  patchValue(storageId: string, value: PrimitiveType, iterationID: number = undefined) {
    if (storageId.includes('/$/')) {
      this.statefulService.wrap(storageId, iterationID).value = value;
    } else {
      this.statefulService.wrap(storageId).value = value;
    }
  }

  isArray(applicant): boolean {
    return Array.isArray(applicant);
  }

  protected readonly onChange = (isAccepted: boolean): void => {
    setTimeout(() => {
      this.field.data.value = isAccepted;
      if (this.field.config.onChange) {
        this.logicService.evaluate(this.field.config.onChange, {
          field: this.field,
          page: this.workflowService.currentPage
        });
      }
    }, 10);
  };

  public chkSelection(disclosure: Disclosure, index: number): void {
    disclosure.isViewed = true;
    if (!disclosure.disclosureAgreeDate) {
      disclosure.disclosureAgreeDate = new Date().toISOString();
      /** patch the agree date instantly to api for persisting the value on page refresh. **/
      this.patchValue(disclosure.agreedDateStorageId, disclosure.disclosureAgreeDate);
    }
    this.disclosures[index] = disclosure;
    if (typeof disclosure.allowToAccept == 'boolean') {
      disclosure.allowToAccept = true;
      return;
    }

    if (typeof disclosure.allowToAccept !== 'string') {
      disclosure.allowToAccept.value = true;
    }
  }

  public getAllowToAccept(disclosure: Disclosure): boolean {
    return disclosure?.isReadToAccept;
  }

  public isAllDocsViewed(): boolean {
    return this.disclosures.filter((t) => !t.disclosureAgreeDate).length <= 0;
  }

  public openSlideInPanel(disclosure: Disclosure) {
    if (this.isCallInitiated) {
      return;
    }
    if (!disclosure.isLinkType && !disclosure.isPDFFile) {
      this.pdfURL = this.content = undefined;
      this.isOpen = true;
      this.content = disclosure.content;
      this.docTitle = disclosure.title;
      this.isCallInitiated = false;
      this.isDataloaded = true;
      return;
    }

    const disclosureFileName = disclosure.isHtmlFile ? disclosure.htmlFileName : disclosure.pdfFileName;
    const mimeType = disclosure.isHtmlFile ? Info.htmlMemeType : Info.pdfMemeType;
    this.isOpen = true;
    this.filename = `${disclosureFileName}.pdf`;
    this.docTitle = disclosure.title;
    this.isDataloaded = false;
    this.download(disclosureFileName, mimeType, disclosure.isPDFFile);
  }

  public download(value: string, mimeType: string, isPdf: boolean): Promise<void> {
    this.isCallInitiated = true;
    this.pdfURL = this.content = undefined;
    return this.globalService
      .downloadLegalDocument(value, mimeType)
      .toPromise()
      .then((blob: Blob) => {
        if (isPdf) {
          this.pdfURL = this.globalService.createBlobUrl(blob, mimeType);
        } else {
          this.apiService.blobToString(blob, (result) => {
            this.content = result;
          });
        }
        Promise.resolve(true);
        this.isDataloaded = true;
        this.isCallInitiated = false;
      })
      .catch((err) => {
        this.isCallInitiated = false;
        this.commonService.errorHandling(err);
        this.isDataloaded = true;
      });
  }

  public printPDF(): void {
    this.globalService.printPDF(this.pdfURL);
  }

  public downloadPDF(): void {
    this.globalService.downloadFilePDF(this.pdfURL, this.filename);
  }

  /** Triggers OTP screen with mobile phone number / email */
  public verifyIdentity(applicantIndex: number) {
    this.page.config.payload = {
      mode: 'check-identity',
      applicantType: this.applicantTypes[applicantIndex].type,
      index: applicantIndex,
      updateVerification: this.updateVerification.bind(this),
      isYouthMembership: this.isYouthMembership
    };
    this.statefulService.wrap('ss:showMFAVerification').value = true;
  }
  /** Update verification flag for applicant */
  updateVerification(index, isVerified) {
    this.disclosures.forEach((t) => {
      t.applicants[index].isVerified = isVerified;
      if (t.applicants[index].isVerifiedStorageId) {
        this.patchValue(t.applicants[index].isVerifiedStorageId, isVerified, index);
      }
    });
    this.statefulService.wrap('ss:showMFAVerification').value = false;
    this.page.config.payload = {};
    if (this.isYouthMembership && !this.userService.isAuthenticated)
      setTimeout(() => {
        this.tokenService.clearCachedToken();
      }, 100);
  }

  public ngOnDestroy(): void {
    if (this.subs && this.subs.length > 0) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
      this.subs = [];
    }
  }
}
